/**
 * Textarea — multi-line text input with role-token styling.
 *
 * Same chrome as `Input` (paper bg, line border, accent focus ring).
 * Auto-grows to content when `autoResize` is set.
 *
 * @see docs/spec/20-store-front-design-system.md R6
 */
import * as React from "react";
import { cn } from "../lib/cn.js";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  autoResize?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({ className, autoResize, onChange, ...props }, ref) {
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

    const setRef = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (autoResize && innerRef.current) {
          innerRef.current.style.height = "auto";
          innerRef.current.style.height = `${innerRef.current.scrollHeight}px`;
        }
        onChange?.(e);
      },
      [autoResize, onChange],
    );

    return (
      <textarea
        ref={setRef}
        className={cn(
          "flex min-h-[80px] w-full rounded-sm border border-line bg-paper px-3 py-2",
          "text-sm text-ink placeholder:text-placeholder",
          "focus-visible:outline-hidden focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent-subtle",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "resize-y",
          className,
        )}
        onChange={handleChange}
        {...props}
      />
    );
  },
);
